import { Fragment, useEffect, useState } from 'react';

import { Divider, Paragraph1, Paragraph2, SmallText } from 'inpock-ui-react';

import { CampaignV2, OrderDataList, OrderLineWithId } from '@/apis';
import { useCampaignQuery, useCampaignV2Query } from '@/hooks';
import { Accordion, Box, Tooltip } from '@/components';
import { thumbnail } from '@/utils';

import * as S from './styled';

interface OrderlinesSectionProps {
  orderDataLists: OrderDataList[];
  version?: string;
}

interface OrderlineProps {
  campaign: CampaignV2;
  orderline: OrderLineWithId;
}

function OrderlineName({ campaign, orderline }: OrderlineProps) {
  const option = campaign.options.find((option) => option.id === orderline.main_option_id);
  const sub_option = option?.sub_options.find((option) => option.id === orderline.sub_option_id);
  const additional_product_name =
    orderline.additional_product_id &&
    campaign.additional_products?.find((product) =>
      product.options.find((option) => option.id === orderline.additional_product_id),
    )?.name;
  const additional_product_option_name = campaign.additional_products
    ?.find((product) =>
      product.options.find((option) => option.id === orderline.additional_product_id),
    )
    ?.options.find((option) => option.id === orderline.additional_product_id)?.option_name;

  return (
    <SmallText color="gray500">
      {orderline.additional_product_id
        ? `[추가 상품] ${additional_product_name} / ${additional_product_option_name} /`
        : option?.name ?? ''}{' '}
      {sub_option ? ` / ${sub_option.name} / ` : ''} {orderline.quantity}개
    </SmallText>
  );
}

function OrderItem({
  campaign_id,
  orderlines,
  product_price,
  version,
}: {
  campaign_id: number;
  orderlines: OrderLineWithId[];
  product_price: number;
  version?: string;
}) {
  const { data: blockV1 } = useCampaignQuery(campaign_id as unknown as string, '', {
    enabled: !version,
  });
  const { data: blockV2 } = useCampaignV2Query(campaign_id as unknown as string, {
    enabled: !!version,
  });
  const block = version ? blockV2 : blockV1;

  if (!block) return null;
  const { campaign } = block;

  return (
    <Box flex sx={{ gap: '10px' }}>
      <S.Img src={thumbnail.small(campaign.images[0]?.image)} />
      <div className="d-flex flex-column" style={{ gap: '5px' }}>
        <Paragraph2 color="gray800" fontWeight="bold">
          {campaign.name}
        </Paragraph2>
        {orderlines.map((orderline, idx) => {
          return <OrderlineName key={idx} campaign={campaign} orderline={orderline} />;
        })}
        <SmallText className="text-bold">{product_price.toLocaleString()}원</SmallText>
      </div>
    </Box>
  );
}

function OrderItems({
  orderDataList,
  version,
}: {
  orderDataList: OrderDataList;
  version?: string;
}) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <S.OrderItemsWrapper>
      {orderDataList.order_data.map((data, idx) => {
        return (
          <Fragment key={data.campaign_id}>
            <OrderItem
              campaign_id={data.campaign_id}
              orderlines={data.orderlines}
              product_price={data.product_price}
              version={version}
            />
            {idx !== orderDataList.order_data.length - 1 && <Divider spacing={20} />}
          </Fragment>
        );
      })}
      <Divider spacing={20} />
      <S.DeliveryFeeContainer>
        <Paragraph2>
          상품 {orderDataList.product_price.toLocaleString()} + 배송비{' '}
          {orderDataList.shipping_fee.toLocaleString()} ={' '}
          <span className="text-bold">{orderDataList.total_price.toLocaleString()}</span>
        </Paragraph2>
        <Tooltip tooltipId="delivery-hint" place="top-start" openOnClick>
          <SmallText className="text-white">
            동일 배송정책이 적용된 상품은
            <br />
            묶음 배송으로 처리되며, <br />
            배송비는 한 번만 부과됩니다.
          </SmallText>
        </Tooltip>
      </S.DeliveryFeeContainer>
    </S.OrderItemsWrapper>
  );
}

export function OrderlinesSection({ orderDataLists, version }: OrderlinesSectionProps) {
  const [firstCampaignId, setFirstCampaignId] = useState<number | null>(null);
  const { data: firstBlockV1 } = useCampaignQuery(firstCampaignId as unknown as string, '', {
    enabled: !version && !!firstCampaignId,
  });
  const { data: firstBlockV2 } = useCampaignV2Query(firstCampaignId as unknown as string, {
    enabled: !!version && !!firstCampaignId,
  });
  const firstBlock = version ? firstBlockV2 : firstBlockV1;
  const { campaign } = firstBlock || {};
  const campaignName =
    campaign?.name && campaign?.name.length > 30
      ? `${campaign?.name.slice(0, 30)}···`
      : campaign?.name;

  useEffect(() => {
    if (orderDataLists.length > 0) {
      setFirstCampaignId(orderDataLists[0].order_data[0].campaign_id);
    }
  }, [orderDataLists, version]);

  const totalOrderlines = orderDataLists.reduce((acc, orderDataList) => {
    orderDataList.order_data.forEach((data) => {
      acc += data.orderlines.length;
    });
    return acc;
  }, 0);

  return (
    <Accordion
      firstRow={<Paragraph1 fontWeight="bold">상품 정보</Paragraph1>}
      secondRow={
        <Paragraph2 color="gray500">{`${campaignName} 총 ${totalOrderlines}건`}</Paragraph2>
      }
      open={true}
    >
      {orderDataLists.map((orderDataList, idx) => {
        return <OrderItems key={idx} orderDataList={orderDataList} version={version} />;
      })}
    </Accordion>
  );
}
